import { get } from 'lodash';
/**
 * Calculates aggregate keyword metrics from section keyword data
 *
 * @param data - Array of SectionKeywords containing content phrases and their metadata
 * @returns Object containing:
 *  - productiveKeywords: Total number of keywords with positive weight
 *  - unproductiveKeywords: Total number of keywords with zero or negative weight
 *  - totalHits: Sum of all keyword hits across all sections
 */

import { KeywordBreakdownData, SummaryProperties } from "src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types";
import { KeywordFilter } from "src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/types";
import { createMetricColumnDefinition } from "src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/utils";

export const getKeywordMetricsForSubtitle = (data: KeywordBreakdownData) => {
  const { sections, summaryScores } = data;
  const uniquePhrases = sections.reduce((acc, section) => {
    section.content.forEach((phrase) => {
      // eslint-disable-next-line no-unused-expressions
      phrase?.metadata?.weights?.forEach((keywordWeight) => {
        acc.add(keywordWeight.keyword);
      });
    });

    return acc;
  }, new Set<string>());
  const uniquePhraseCount = uniquePhrases.size;

  const result = sections
    .map((section) => {
      const { content } = section;
      return content.reduce(
        (acc, phrase) => {
          const { metadata } = phrase;
          if (!metadata) {
            return acc;
          }

          const { totalWeight, weights: keywordWeights } = metadata;
          if (!keywordWeights) {
            if (totalWeight > 0) {
              return {
                ...acc,
                productiveKeywords: acc.productiveKeywords + 1,
                totalHits: acc.totalHits + totalWeight
              };
            } else {
              return {
                ...acc,
                unproductiveKeywords: acc.unproductiveKeywords + 1,
                totalHits: acc.totalHits + totalWeight
              };
            }
          }
          // @ts-ignore
          const productiveKeywords = keywordWeights.filter((keywordWeight) => keywordWeight.weight > 0).length;
          // @ts-ignore
          const unproductiveKeywords = keywordWeights.filter((keywordWeight) => keywordWeight.weight <= 0).length;
          return {
            productiveKeywords: acc.productiveKeywords + productiveKeywords,
            unproductiveKeywords: acc.unproductiveKeywords + unproductiveKeywords,
            totalHits: acc.totalHits + totalWeight
          };
        },
        { productiveKeywords: 0, unproductiveKeywords: 0, totalHits: 0 }
      );
    })
    .reduce(
      (acc, curr) => {
        return {
          productiveKeywords: acc.productiveKeywords + curr.productiveKeywords,
          unproductiveKeywords: acc.unproductiveKeywords + curr.unproductiveKeywords,
          totalHits: acc.totalHits + curr.totalHits
        };
      },
      { productiveKeywords: 0, unproductiveKeywords: 0, totalHits: 0 }
    );

  return {
    ...result,
    totalKeywords: summaryScores.averageCountOfKeywords,
    numProductiveKeywords: summaryScores.averageCountOfKeywordsGeneratingTraffic,
    numUnproductiveKeywords:
      summaryScores.averageCountOfKeywords - summaryScores.averageCountOfKeywordsGeneratingTraffic,
    totalSearchCount: summaryScores.averageVolumeOfSearchTraffic,
    totalUniqueKeywords: uniquePhraseCount
  };
};

export const DefaultColumnDefinitions = [
  [SummaryProperties.keywordUsageScore.id, SummaryProperties.keywordUsageScore.header], // group 1 (Usage)
  [SummaryProperties.keywordProductivityScore.id, SummaryProperties.keywordProductivityScore.header], // group 2 (Productivity)
  [SummaryProperties.keywordPopularityScore.id, SummaryProperties.keywordPopularityScore.header], // group 3 (Popularity)
].map(([id, header]) => createMetricColumnDefinition([id, header]));

export const getSplineChartTitle = (isProductEntity: boolean, keywordFilter: KeywordFilter) => {
  const byWeek = ' by Week';
  switch (keywordFilter) {
    case KeywordFilter.Usage:
      return isProductEntity ? SummaryCardTilesHeaders[keywordFilter].product + byWeek : SummaryCardTilesHeaders[keywordFilter].default + byWeek;
    case KeywordFilter.Productivity:
      return isProductEntity ? SummaryCardTilesHeaders[keywordFilter].product + byWeek : SummaryCardTilesHeaders[keywordFilter].default + byWeek;
    case KeywordFilter.Popularity:
      return isProductEntity ? SummaryCardTilesHeaders[keywordFilter].product + byWeek : SummaryCardTilesHeaders[keywordFilter].default + byWeek;
    case KeywordFilter.UnProductiveKeywords:
      return isProductEntity ? SummaryCardTilesHeaders[keywordFilter].product + byWeek : SummaryCardTilesHeaders[keywordFilter].default + byWeek;
    default:
      return '';
  }
};

export const WeekContentMetricType = {
  score: 'score',
  averageCountOfKeywords: 'averageCountOfKeywords',
  averageSearchVolume: 'averageSearchVolume'
};

// Create a dynamic type from WeekContentMetricType values
export type RecordPropertyType = typeof WeekContentMetricType[keyof typeof WeekContentMetricType];

// Metric property mapping are subjected to change in near future
export const getWeekRecordMetricPropertyName = (keywordFilter: KeywordFilter) => {
  switch (keywordFilter) {
    case KeywordFilter.Usage:
    case KeywordFilter.Productivity:
    case KeywordFilter.UnProductiveKeywords:
      return WeekContentMetricType.averageCountOfKeywords;
    case KeywordFilter.Popularity:
      return WeekContentMetricType.averageSearchVolume;
    default:
      return WeekContentMetricType.score;
  }
};

export const SummaryCardTilesHeaders = {
  [KeywordFilter.Usage]: {
    default: 'AVG Keywords Used',
    product: 'Keywords Used'
  }, // group 1 (Usage)
  [KeywordFilter.KeywordBenchmark]: {
    default: 'Keyword Benchmark',
    product: 'Keyword Benchmark'
  }, // group 1 (Usage)
  [KeywordFilter.LowUsage]: {
    default: 'Low Usage',
    product: 'Low Usage'
  }, // group 1 (Usage)
  [KeywordFilter.Productivity]: {
    default: 'AVG Productive Keywords',
    product: 'Productive Keywords'
  }, // group 2 (Productivity)
  [KeywordFilter.UnProductiveKeywords]: {
    default: 'AVG Unproductive Keywords',
    product: 'Unproductive Keywords'
  }, // group 2 (Productivity)
  [KeywordFilter.LowProductivity]: {
    default: 'Low Productivity',
    product: 'Low Productivity'
  }, // group 2 (Productivity)
  [KeywordFilter.Popularity]: {
    default: 'AVG Keyword Search Volume',
    product: 'Keyword Search Volume'
  }, // group 3 (Popularity)
  [KeywordFilter.PopularityBenchmark]: {
    default: 'Popularity Benchmark',
    product: 'Popularity Benchmark'
  }, // group 3 (Popularity)
  [KeywordFilter.LowPopularity]: {
    default: 'Low Popularity',
    product: 'Low Popularity'
  } // group 3 (Popularity)
};
